import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { UserDocumentsRoutes } from 'src/app/core/enums/routes.enum';
import { Privileges } from 'src/app/core/privileges/privileges';
import { getTenantId } from 'src/app/services/store-snapshot.service';
import { IDepartmentInfo } from 'src/app/shared/user-view/models/tenant-user-access.model';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import {
  ChangeCurrentSelectedDepartment,
  ChangeCurrentSelectedTenant,
} from 'src/app/tenant-selector/state/tenant-user-access.actions';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import {
  DocumentWorkingCopyToastComponent,
  DocumentWorkingCopyToastData,
} from '../components/document-working-copy-toast/document-working-copy-toast.component';
import { getLinkToDocumentByPublicationState } from '../functions/get-link-to-document';
import { hasWorkingCopyId } from '../functions/has-working-copy-id';
import { workingCopyDepartmentToActivate } from '../functions/working-copy-department-to-activate';
import { DocumentWorkingCopyDepartmentDialogComponent } from '../modals/document-working-copy-department-dialog/document-working-copy-department-dialog.component';
import { IDocumentWorkingCopy } from '../models/document-working-copy.model';
import { IDocument } from '../models/document.model';
import {
  ClearDocumentWorkingCopy,
  DeleteDocumentWorkingCopy,
  LoadLatestDocumentWorkingCopy,
  SetDocumentWorkingCopyIntent,
} from '../state/document-working-copy.actions';
import { DocumentWorkingCopyState } from '../state/document-working-copy.state';
import { DocumentApiService } from './document-api.service';

interface WorkingCopyToastContext {
  documentName: string;
  document: IDocument | null;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentWorkingCopyReminderService {
  private hasPrompted = false;
  private toastContext?: WorkingCopyToastContext;
  private toastRef?: MatSnackBarRef<DocumentWorkingCopyToastComponent>;

  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private translate: TranslateService,
    private documentApi: DocumentApiService,
    private tenantUserPrivileges: TenantUserPrivilegesService
  ) {
    this.actions$.pipe(ofActionSuccessful(ChangeCurrentSelectedTenant)).subscribe(() => {
      this.refreshAndPrompt();
    });
    this.actions$
      .pipe(ofActionSuccessful(SetDocumentWorkingCopyIntent, DeleteDocumentWorkingCopy))
      .subscribe((action) => this.dismissToastForResolvedWorkingCopy(action));
  }

  promptIfNeeded(): void {
    if (this.hasPrompted || !getTenantId()) {
      return;
    }

    this.refreshAndPrompt();
  }

  getDisplayName(workingCopy: IDocumentWorkingCopy | undefined): Observable<string> {
    const fallback = this.translate.instant('newDocument');
    if (!workingCopy) {
      return of(fallback);
    }

    return this.resolveToastContext(workingCopy).pipe(
      map((context) => context?.documentName || workingCopy.documentName || fallback)
    );
  }

  resumeWorkingCopy(): void {
    const department = this.departmentToActivate();
    if (department) {
      this.confirmDepartmentChange(department);
      return;
    }

    this.openStoredWorkingCopy();
  }

  private refreshAndPrompt(): void {
    if (!getTenantId() || !this.tenantUserPrivileges.userHasPrivilege(Privileges.DocumentCreate)) {
      return;
    }

    this.hasPrompted = true;
    this.dismissWorkingCopyToast();
    this.store
      .dispatch([new ClearDocumentWorkingCopy(), new LoadLatestDocumentWorkingCopy()])
      .pipe(switchMap(() => this.showToastIfPresent()))
      .subscribe();
  }

  private showToastIfPresent(): Observable<unknown> {
    const workingCopy = this.store.selectSnapshot(DocumentWorkingCopyState.getWorkingCopy);
    if (!hasWorkingCopyId(workingCopy) || this.isViewingWorkingCopy(workingCopy)) {
      return of(undefined);
    }

    return this.resolveToastContext(workingCopy).pipe(
      tap((context) => {
        if (context) {
          this.openToast(context);
        }
      })
    );
  }

  private resolveToastContext(
    workingCopy: IDocumentWorkingCopy
  ): Observable<WorkingCopyToastContext | undefined> {
    const documentId = workingCopy.documentId ?? '';
    if (!hasWorkingCopyId({ id: documentId })) {
      return of({
        documentName: workingCopy.documentName || this.translate.instant('newDocument'),
        document: null,
      });
    }

    return this.documentApi.loadDocumentById(documentId).pipe(
      map((document) => ({
        documentName:
          document.name || workingCopy.documentName || this.translate.instant('newDocument'),
        document,
      })),
      catchError(() => of(undefined))
    );
  }

  private openToast(context: WorkingCopyToastContext): void {
    this.toastContext = context;
    this.toastRef = this.snackBar.openFromComponent<
      DocumentWorkingCopyToastComponent,
      DocumentWorkingCopyToastData
    >(DocumentWorkingCopyToastComponent, {
      data: { documentName: context.documentName },
      duration: 0,
      panelClass: 'document-working-copy-toast',
    });
    this.toastRef
      .onAction()
      .pipe(take(1))
      .subscribe(() => this.resumeWorkingCopy());
  }

  private dismissToastForResolvedWorkingCopy(
    action: SetDocumentWorkingCopyIntent | DeleteDocumentWorkingCopy
  ): void {
    if (action instanceof DeleteDocumentWorkingCopy) {
      this.dismissWorkingCopyToast();
      return;
    }

    if (action.intent === 'resume' || action.intent === 'startFresh') {
      this.dismissWorkingCopyToast();
    }
  }

  private dismissWorkingCopyToast(): void {
    this.toastRef?.dismiss();
    this.toastRef = undefined;
    this.toastContext = undefined;
  }

  private departmentToActivate(): IDepartmentInfo | undefined {
    const workingCopy = this.store.selectSnapshot(DocumentWorkingCopyState.getWorkingCopy);
    return workingCopyDepartmentToActivate(
      workingCopy?.departmentIds,
      this.store.selectSnapshot(TenantUserAccessState.currentDepartmentId),
      this.store.selectSnapshot(TenantUserAccessState.getAllAvailableDepartments)
    );
  }

  private confirmDepartmentChange(department: IDepartmentInfo): void {
    this.dialog
      .open(DocumentWorkingCopyDepartmentDialogComponent, {
        width: '36rem',
        panelClass: ['no-padding', 'mobile-sizing-for-mat-dialogs'],
        data: { departmentName: department.name },
      })
      .afterClosed()
      .subscribe((confirmed: boolean | undefined) =>
        this.afterDepartmentChangeConfirmed(confirmed, department.id)
      );
  }

  private afterDepartmentChangeConfirmed(
    confirmed: boolean | undefined,
    departmentId: string
  ): void {
    if (confirmed !== true) {
      this.reopenToast();
      return;
    }

    this.store
      .dispatch(new ChangeCurrentSelectedDepartment(departmentId, false))
      .subscribe(() => this.openStoredWorkingCopy());
  }

  private openStoredWorkingCopy(): void {
    const contextDocument = this.toastContext?.document;
    this.store.dispatch(new SetDocumentWorkingCopyIntent('resume'));
    if (contextDocument) {
      this.navigateToDocument(contextDocument);
      return;
    }

    this.navigateToStoredWorkingCopy();
  }

  private reopenToast(): void {
    if (!this.toastContext) {
      return;
    }

    this.openToast(this.toastContext);
  }

  private navigateToStoredWorkingCopy(): void {
    const workingCopy = this.store.selectSnapshot(DocumentWorkingCopyState.getWorkingCopy);
    const documentId = workingCopy?.documentId ?? '';
    if (!hasWorkingCopyId({ id: documentId })) {
      this.router.navigate([UserDocumentsRoutes.NewDocument]);
      return;
    }

    this.documentApi
      .loadDocumentById(documentId)
      .pipe(catchError(() => of(null)))
      .subscribe((document) => {
        if (!document) {
          this.router.navigate([UserDocumentsRoutes.NewDocument]);
          return;
        }

        this.navigateToDocument(document);
      });
  }

  private navigateToDocument(document: IDocument): void {
    const { link, referrerType } = getLinkToDocumentByPublicationState(document);
    this.router.navigate([link], { queryParams: { referrerType } });
  }

  private isViewingWorkingCopy(workingCopy: IDocumentWorkingCopy): boolean {
    const url = this.router.url.split('?')[0];
    const documentId = workingCopy.documentId ?? '';
    if (hasWorkingCopyId({ id: documentId })) {
      return url.includes(documentId);
    }

    return url.startsWith(UserDocumentsRoutes.NewDocument);
  }
}
